import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signup, saveUserLocation, fetchLocationsByRole } from '../apiService';
import Carousel from './Carousel';

const SignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '', email: '', phone: '', password: '', confirmPassword: '',
    type: '', locationId: '', latitude: '', longitude: '',
  });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const roleToApiType = {
    'Manufacturer': ['Manufacturer'],
    'Recycler': ['Recycler'],
    'Distributor': ['Distributor'],
    'Retailer': ['Retailer'],
    'Regulator': ['Regulator'],
    'Repair/Refurb': ['Repairer', 'Refurbisher'],
    'Customer': ['Customer', 'EndUser'],
    'Manufacturer': ['Manufacturer','Manufacturing']
  };

  useEffect(() => {
    if (!formData.type) {
      setLocations([]);
      setFormData(prev => ({ ...prev, locationId: '' }));
      return;
    }
    const fetchLocations = async () => {
      setLocationsLoading(true);
      setFormData(prev => ({ ...prev, locationId: '' }));
      try {
        const data = await fetchLocationsByRole();
        if (data.success) {
          setLocations(data.locations.filter(loc => roleToApiType[formData.type].includes(loc.locations_type)));
        }
      } catch (err) {
        console.error('Failed to fetch locations:', err);
        setLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, [formData.type]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setNotification(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotification(null);

    if (!formData.username || !formData.email || !formData.phone || !formData.password || !formData.confirmPassword || !formData.type || !formData.locationId) {
      setNotification({ message: 'Please fill in all fields', type: 'error' }); return;
    }
    if (!formData.email.includes('@')) {
      setNotification({ message: 'Please enter a valid email address', type: 'error' }); return;
    }
    if (formData.password.length < 6) {
      setNotification({ message: 'Password must be at least 6 characters', type: 'error' }); return;
    }
    if (formData.password !== formData.confirmPassword) {
      setNotification({ message: 'Passwords do not match', type: 'error' }); return;
    }

    setLoading(true);
    try {
      const response = await signup(formData.username, formData.email, formData.phone, formData.password, formData.type);
      if (!response.success) throw new Error(response.message || 'Signup failed');

      const locationResponse = await saveUserLocation({
        user_id: response.data.id,
        location_id: formData.locationId,
        latitude: formData.latitude,
        longitude: formData.longitude,
      });
      if (!locationResponse.success) throw new Error(locationResponse.message || 'Failed to save location');

      localStorage.setItem('pendingLoginEmail', formData.email);
      setNotification({ message: response.message || 'Account created successfully! Redirecting to login...', type: 'success' });
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      setNotification({ message: error.message || 'Signup failed. Please try again.', type: 'error' });
      console.error('Signup error:', error);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700 focus:outline-none focus:border-blue-500 focus:bg-white transition-colors duration-200 box-border";
  const selectClass = `${inputClass} cursor-pointer`;

  return (
    <div className="flex h-screen w-screen bg-white overflow-hidden">
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .notification-slide { animation: slideDown 0.3s ease-out; }

        /* Carousel overrides so it fills the blue left panel */
        .signup-left .carouselContainer,
        .signup-left [class*="carouselContainer"] {
          height: 100%;
          justify-content: space-between;
        }
      `}</style>

      {/* ── Left: Carousel (hidden on mobile, shown md+) ── */}
      <div className="signup-left hidden md:flex md:flex-1 bg-[#326BDF] items-center justify-center p-4 overflow-hidden">
        <Carousel initialSlide={1} />
      </div>

      {/* ── Right: Signup Form ── */}
      <div className="flex-1 bg-[#EDEDED] flex items-start md:items-center justify-center overflow-y-auto py-6 px-4 md:px-6">
        <div className="w-full max-w-md">

          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src="/Provinyx-logo-LightTheme.png"
              alt="Provinyx"
              className="h-10 md:h-12 w-auto"
            />
          </div>

          {/* Form Card */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 md:p-8 shadow-sm">
            <form className="flex flex-col gap-3" onSubmit={handleSubmit}>

              <input
                type="text" name="username" placeholder="Username"
                value={formData.username} onChange={handleChange}
                className={inputClass} disabled={loading}
              />
              <input
                type="email" name="email" placeholder="Email"
                value={formData.email} onChange={handleChange}
                className={inputClass} disabled={loading}
              />
              <input
                type="tel" name="phone" placeholder="Phone"
                value={formData.phone} onChange={handleChange}
                className={inputClass} disabled={loading}
              />
              <input
                type="password" name="password" placeholder="Password"
                value={formData.password} onChange={handleChange}
                className={inputClass} disabled={loading}
              />
              <input
                type="password" name="confirmPassword" placeholder="Confirm Password"
                value={formData.confirmPassword} onChange={handleChange}
                className={inputClass} disabled={loading}
              />

              {/* Role */}
              <select
                name="type" value={formData.type} onChange={handleChange}
                className={selectClass} disabled={loading}
              >
                <option value="" disabled>Select Role</option>
                <option value="Manufacturer">Manufacturer</option>
                <option value="Recycler">Recycler</option>
                <option value="Distributor">Distributor</option>
                <option value="Retailer">Retailer</option>
                <option value="Regulator">Regulator</option>
                <option value="Repair/Refurb">Repair/Refurb</option>
                <option value="Customer">Customer</option>
              </select>

              {/* Location — conditional */}
              {formData.type && (
                <select
                  name="locationId"
                  value={formData.locationId}
                  onChange={(e) => {
                    const selected = locations.find(loc => loc.locationId === e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      locationId: selected.locationId,
                      latitude: selected.latitude,
                      longitude: selected.longitude,
                    }));
                    setNotification(null);
                  }}
                  className={selectClass}
                  disabled={loading || locationsLoading}
                >
                  <option value="" disabled>
                    {locationsLoading
                      ? 'Loading locations...'
                      : locations.length === 0
                      ? 'No locations for this role'
                      : 'Select Location ID'}
                  </option>
                  {locations.map(loc => (
                    <option key={loc.locationId} value={loc.locationId}>{loc.locationId}</option>
                  ))}
                </select>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 bg-[#326BDF] text-white border-none rounded-lg py-3 px-4 text-sm font-semibold cursor-pointer transition-colors duration-200 hover:bg-[#2859c5] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing Up...' : 'Sign Up'}
              </button>
            </form>

            {/* Notification */}
            {notification && (
              <div className={`notification-slide mt-5 p-4 rounded-lg flex items-center justify-between ${
                notification.type === 'success'
                  ? 'bg-green-50 border border-green-300 text-green-800'
                  : 'bg-red-50 border border-red-300 text-red-800'
              }`}>
                <span className="font-medium text-sm">{notification.message}</span>
                <button
                  onClick={() => setNotification(null)}
                  className="bg-transparent border-none text-2xl font-bold cursor-pointer opacity-70 ml-4 leading-none hover:opacity-100 text-inherit"
                >×</button>
              </div>
            )}
          </div>

          {/* Login link */}
          <div className="mt-4 text-left text-sm text-gray-500">
            Already have an account?{' '}
            <span
              onClick={() => navigate('/')}
              className="text-gray-500 cursor-pointer hover:text-[#326BDF] hover:underline transition-colors duration-200"
            >
              Log In Here
            </span>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SignUp;